import { createContext, useContext, useState, useEffect, ReactNode, useMemo, useRef, useCallback } from 'react';
import axios from '../../axios-config';
import { House, Transaction, AppConfig, defaultConfig } from '../data/houses';
import { useOrganizations } from '../../contexts/OrganizationsContext';
import { startNotificationConnection } from '../../services/notificationHubService';
import { UserContext } from '../../contexts/UserContext';

interface AppState {
  houses: House[];
  transactions: Transaction[];
  config: AppConfig;
}

interface AppContextType {
  houses: House[];
  activeHouses: House[];
  transactions: Transaction[];
  config: AppConfig;
  getHouse: (id: string) => House | undefined;
  updateHouse: (id: string, data: Partial<House>) => void;
  setActiveHouseCount: (count: number) => void;
  addPoints: (houseId: string, points: number, source: 'manual' | 'coupon') => Transaction;
  getTransactions: (houseId?: string) => Transaction[];
  getMascotUserId: (houseId: string) => string | undefined;
  lastUpdated: Date | null;
  autoRefreshEnabled: boolean;
  autoRefreshInterval: number;
  timeUntilNextRefresh: number;
  setAutoRefreshEnabled: (enabled: boolean) => void;
  setAutoRefreshInterval: (interval: number) => void;
  forceRefresh: () => Promise<void>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

const STORAGE_KEY = 'houseKioskState';
const HOUSE_POINTS_LAST_EVENT_KEY = 'housekiosk:lastHousePointsEventByHouseId';

type HousePointsLastEventMeta = {
  lastOccurredAtUtc?: number;
  lastEventId?: string | number;
};

function loadHousePointsLastEventMap(): Map<string, HousePointsLastEventMeta> {
  if (typeof window === 'undefined') return new Map();
  try {
    const raw = window.sessionStorage.getItem(HOUSE_POINTS_LAST_EVENT_KEY);
    if (!raw) return new Map();
    const parsed = JSON.parse(raw) as Record<string, HousePointsLastEventMeta>;
    const entries = Object.entries(parsed ?? {}).map(([houseId, meta]) => [String(houseId), meta] as const);
    return new Map(entries);
  } catch {
    return new Map();
  }
}

function persistHousePointsLastEvent(houseId: string, meta: HousePointsLastEventMeta) {
  if (typeof window === 'undefined') return;
  try {
    const storage = window.sessionStorage;
    const raw = storage.getItem(HOUSE_POINTS_LAST_EVENT_KEY);
    const parsed = (raw ? JSON.parse(raw) : {}) as Record<string, HousePointsLastEventMeta>;
    parsed[String(houseId)] = meta;
    storage.setItem(HOUSE_POINTS_LAST_EVENT_KEY, JSON.stringify(parsed));
  } catch {
    // ignore storage failures
  }
}

function iconToEmoji(icon?: string): string {
  switch (icon) {
    case 'leaf':
      return '🌿';
    case 'fire':
      return '🔥';
    case 'raindrop':
      return '💧';
    case 'sun':
      return '☀️';
    case 'snowflake':
      return '❄️';
    default:
      return icon || '🏠';
  }
}

function saveToStorage(state: AppState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    console.error('Failed to save state to localStorage:', e);
  }
}

export function AppProvider({ children }: { children: ReactNode }) {
  const { organizations, fetchOrganizations } = useOrganizations();
  const { userProfile } = useContext(UserContext);
  const organizationsRef = useRef(organizations);
  const lastHouseEventRef = useRef<Map<string, { lastOccurredAtUtc?: number; lastEventId?: string | number }>>(new Map());

  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [autoRefreshEnabled, setAutoRefreshEnabledState] = useState(() => {
    const saved = localStorage.getItem('housekiosk-auto-refresh-enabled');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [autoRefreshInterval, setAutoRefreshIntervalState] = useState(() => {
    const saved = localStorage.getItem('housekiosk-auto-refresh-interval');
    return saved !== null ? parseInt(saved, 10) : 30000;
  });
  const [timeUntilNextRefresh, setTimeUntilNextRefresh] = useState(0);

  const autoRefreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastRefreshTimeRef = useRef<Date>(new Date());

  useEffect(() => {
    organizationsRef.current = organizations;
  }, [organizations]);

  // Persisted "last event seen" metadata prevents remounts from treating older org points as newer.
  useEffect(() => {
    lastHouseEventRef.current = loadHousePointsLastEventMap();
  }, []);

  useEffect(() => {
    localStorage.setItem('housekiosk-auto-refresh-enabled', JSON.stringify(autoRefreshEnabled));
  }, [autoRefreshEnabled]);

  useEffect(() => {
    localStorage.setItem('housekiosk-auto-refresh-interval', String(autoRefreshInterval));
  }, [autoRefreshInterval]);

  const setAutoRefreshEnabled = (enabled: boolean) => {
    setAutoRefreshEnabledState(enabled);
  };

  const setAutoRefreshInterval = (interval: number) => {
    setAutoRefreshIntervalState(interval);
  };

  // Map organizations of type 9 (houses) to House objects
  const orgHouses = useMemo<House[]>(() => {
    return organizations
      .filter(org => org.organizationTypeId === 9)
      .map(org => {
        const mascotUser = org.organizationUsers?.map((ou: any) => ou?.acornUser).find((u: any) => (
          u?.organizationUserTypeId === 7 || u?.mainRole === 'MASCOT' || u?.role === 'MASCOT'
        ));
        const points = Math.round(
          (mascotUser?.points as number | undefined) ?? (org.pointsAwarded as number | undefined) ?? 0
        );

        return {
          id: org.organizationId,
          name: org.organizationName,
          icon: iconToEmoji(org.icon),
          points,
          color: org.color ?? '#ffffff',
          accent: org.accent ?? '#cccccc',
          gemGradient: org.gemGradient ?? 'linear-gradient(180deg, #ffffff 0%, #cccccc 100%)',
          backgroundImage: org.backgroundImageUrl,
          isArchived: !!(org.deActivatedOn ?? org.deactivatedOn),
          deactivatedOn: (org.deActivatedOn ?? org.deactivatedOn) ? String(org.deActivatedOn ?? org.deactivatedOn) : null,
        };
      });
  }, [organizations]);

  const [state, setState] = useState<AppState>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        parsed.transactions = (parsed.transactions ?? []).map((t: Transaction) => ({
          ...t,
          timestamp: new Date(t.timestamp),
        }));
        return {
          houses: parsed.houses ?? orgHouses,
          transactions: parsed.transactions ?? [],
          config: parsed.config ?? defaultConfig,
        } as AppState;
      }
    } catch (e) {
      console.error('Failed to load state from localStorage:', e);
    }
    return {
      houses: orgHouses,
      transactions: [],
      config: defaultConfig,
    } as AppState;
  });

  const isHouseActive = (houseId: string, fallbackHouse?: House): boolean => {
    const org = organizationsRef.current.find((o: any) => String(o?.organizationId) === String(houseId));
    if (org) {
      return !org?.deActivatedOn && !org?.deactivatedOn;
    }
    return !fallbackHouse?.isArchived && !fallbackHouse?.deactivatedOn;
  };

  useEffect(() => {
    saveToStorage(state);
  }, [state]);

  // Live updates for mascot/house points via SignalR
  useEffect(() => {
    const resolveHousePointsScopeOrgId = (): string | undefined => {
      const orgId =
        (userProfile as any)?.orgId ??
        (Array.isArray((userProfile as any)?.orgs) ? (userProfile as any).orgs[0] : (userProfile as any)?.orgs) ??
        undefined;

      if (!orgId) return undefined;

      const org = organizationsRef.current.find((o: any) => String(o.organizationId) === String(orgId));

      // If user is in a House (Team) org, subscribe at the parent (class) level
      if (org?.organizationTypeId === 9 && org?.parentOrganizationId) {
        return String(org.parentOrganizationId);
      }

      // Otherwise subscribe to their org directly (class/teacher/kiosk)
      return String(orgId);
    };

    const resolveHouseIdFromPayload = (payload: any): string | undefined => {
      const directId =
        payload?.houseId ??
        payload?.houseOrganizationId ??
        payload?.houseOrgId ??
        payload?.teamOrganizationId ??
        payload?.organizationId ??
        payload?.orgId ??
        payload?.house?.houseId ??
        payload?.house?.organizationId ??
        payload?.house?.orgId;

      if (directId) return String(directId);

      // fallback: name match (common for teacher/student payload shapes)
      const name =
        payload?.houseName ??
        payload?.organizationName ??
        payload?.teamName ??
        payload?.house?.name ??
        payload?.house?.organizationName;

      if (name) {
        const normalized = String(name).trim().toLowerCase();
        const byName = organizationsRef.current.find((o: any) =>
          o?.organizationTypeId === 9 &&
          String(o?.organizationName ?? '').trim().toLowerCase() === normalized
        );
        if (byName?.organizationId) return String(byName.organizationId);
      }

      // last resort: map mascotUserId -> house via org users (if present)
      const mascotUserId = payload?.mascotUserId || payload?.studentId || payload?.userId || payload?.acornUserId;
      if (!mascotUserId) return undefined;

      const org = organizationsRef.current.find((o: any) =>
        o.organizationTypeId === 9 &&
        o.organizationUsers?.some((ou: any) => String(ou?.acornUser?.id) === String(mascotUserId))
      );
      return org?.organizationId;
    };

    const isNewerEvent = (houseId: string, payload: any): boolean => {
      const entry = lastHouseEventRef.current.get(houseId);
      const occurredAt = payload?.lastOccurredAtUtc ? Date.parse(payload.lastOccurredAtUtc) : undefined;
      const eventId = payload?.lastEventId ?? payload?.eventId ?? payload?.id;

      if (occurredAt && Number.isFinite(occurredAt)) {
        if (entry?.lastOccurredAtUtc && occurredAt <= entry.lastOccurredAtUtc) return false;
        const next: HousePointsLastEventMeta = { lastOccurredAtUtc: occurredAt, lastEventId: eventId ?? entry?.lastEventId };
        lastHouseEventRef.current.set(houseId, next);
        persistHousePointsLastEvent(houseId, next);
        return true;
      }

      if (eventId != null) {
        const eventIdNum = Number(eventId);
        const lastIdNum = Number(entry?.lastEventId);
        if (Number.isFinite(eventIdNum) && Number.isFinite(lastIdNum)) {
          if (eventIdNum <= lastIdNum) return false;
        } else if (entry?.lastEventId != null && String(eventId) === String(entry.lastEventId)) {
          return false;
        }
        const next: HousePointsLastEventMeta = { lastOccurredAtUtc: entry?.lastOccurredAtUtc, lastEventId: eventId };
        lastHouseEventRef.current.set(houseId, next);
        persistHousePointsLastEvent(houseId, next);
        return true;
      }

      return true;
    };

    const handleHousePointsPayload = (payload: any) => {
      console.log('[HousePoints] Received house points payload:', payload);
      
      const houseId = resolveHouseIdFromPayload(payload);
      if (!houseId) {
        console.warn('[HousePoints] Could not resolve houseId from payload');
        return;
      }

      if (!isHouseActive(houseId)) {
        console.log('[HousePoints] Ignoring event for deactivated house:', houseId);
        return;
      }
      
      if (!isNewerEvent(houseId, payload)) {
        console.log('[HousePoints] Ignoring stale event for house:', houseId);
        return;
      }

      const points = Number(payload?.points);
      const pointsAdded = Number(payload?.pointsAdded);

      if (Number.isFinite(points)) {
        console.log('[HousePoints] Updating house', houseId, 'to', points, 'points');
        updateHouse(houseId, { points });
      } else if (Number.isFinite(pointsAdded)) {
        console.log('[HousePoints] Updating house', houseId, 'by delta', pointsAdded);
        // fallback if only delta is provided
        setState(prev => ({
          ...prev,
          houses: prev.houses.map(h =>
            h.id === houseId ? { ...h, points: h.points + pointsAdded } : h
          ),
        }));
      } else {
        console.warn('[HousePoints] No valid points or pointsAdded in payload:', payload);
      }
    };

    const handleHousePointsEvent = (event: Event) => {
      const detail = (event as CustomEvent)?.detail;
      if (detail) {
        handleHousePointsPayload(detail);
      }
    };

    window.addEventListener('mascotPointsUpdated', handleHousePointsEvent);

    (async () => {
      const scopeOrgId = resolveHousePointsScopeOrgId();
      if (!scopeOrgId) return;

      await startNotificationConnection(
        undefined,
        'nonStudent',
        undefined,
        undefined,
        handleHousePointsPayload,
        String(scopeOrgId),
        true
      );
    })();

    return () => {
      window.removeEventListener('mascotPointsUpdated', handleHousePointsEvent);
    };
  }, [
    userProfile,
    organizations
  ]);

  // Keep houses in sync when organizations change
  useEffect(() => {
    setState(prev => ({
      ...prev,
      config: {
        ...prev.config,
        activeHouseCount: Math.max(prev.config.activeHouseCount, orgHouses.length),
      },
      houses: orgHouses.map(orgHouse => {
        const existing = prev.houses.find(h => h.id === orgHouse.id);

        // Keep points from existing state (SignalR/localStorage),
        // but allow server/org points to move it forward if higher.
        const mergedPoints = existing ? Math.max(existing.points, orgHouse.points) : orgHouse.points;
        return {
          ...orgHouse,
          points: mergedPoints,
        };
      }),
    }));
  }, [orgHouses]);

  const activeHouses = state.houses
    .filter(h => !h.isArchived && !h.deactivatedOn)
    .slice(0, state.config.activeHouseCount);

  const getHouse = (id: string) => state.houses.find(h => h.id === id);

  const updateHouse = (id: string, data: Partial<House>) => {
    setState(prev => ({
      ...prev,
      houses: prev.houses.map(h => (h.id === id ? { ...h, ...data } : h)),
    }));
  };

  const setActiveHouseCount = (count: number) => {
    setState(prev => {
      const newHouses = prev.houses.map((house, index) => ({
        ...house,
        isArchived: index >= count,
      }));
      return {
        ...prev,
        houses: newHouses,
        config: { ...prev.config, activeHouseCount: count },
      };
    });
  };

  const addPoints = (houseId: string, points: number, source: 'manual' | 'coupon'): Transaction => {
    if (!isHouseActive(houseId, state.houses.find(h => h.id === houseId))) {
      return {
        id: `txn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        houseId,
        pointsDelta: 0,
        timestamp: new Date(),
        source,
      };
    }
    const transaction: Transaction = {
      id: `txn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      houseId,
      pointsDelta: points,
      timestamp: new Date(),
      source,
    };

    setState(prev => ({
      ...prev,
      houses: prev.houses.map(h =>
        h.id === houseId ? { ...h, points: h.points + points } : h
      ),
      transactions: [transaction, ...prev.transactions],
    }));

    // Also award/subtract points to the house mascot user on the server
    try {
      const org = organizations.find(o => o.organizationId === houseId);
      const mascot = org?.organizationUsers?.map((ou: any) => ou.acornUser)?.find((u: any) => (
        u?.organizationUserTypeId === 7 || u?.mainRole === 'MASCOT' || u?.role === 'MASCOT'
      ));
      if (mascot?.id && points !== 0) {
        // Fire-and-forget; StudentCard expects new balance but we keep local optimistic update
        void axios.post(
          `/api/AcornTransaction?studentId=${mascot.id}&points=${points}`,
          undefined,
          { timeout: 10000, skipLoading: true }
        )
          .catch(err => console.error('Mascot award failed:', err));
      }
    } catch (e) {
      console.error('Error processing mascot award:', e);
    }

    return transaction;
  };

  const getTransactions = (houseId?: string) => {
    if (houseId) {
      return state.transactions.filter(t => t.houseId === houseId);
    }
    return state.transactions;
  };

  const getMascotUserId = (houseId: string): string | undefined => {
    if (!isHouseActive(houseId, state.houses.find(h => h.id === houseId))) {
      return undefined;
    }
    const org = organizations.find(o => o.organizationId === houseId);
    const mascot = org?.organizationUsers?.map((ou: any) => ou.acornUser)?.find((u: any) => (
      u?.organizationUserTypeId === 7 || u?.mainRole === 'MASCOT' || u?.role === 'MASCOT'
    ));
    return mascot?.id;
  };

  const startCountdownTimer = useCallback(() => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
    }

    if (!autoRefreshEnabled) {
      setTimeUntilNextRefresh(0);
      return;
    }

    const updateCountdown = () => {
      const elapsed = Date.now() - lastRefreshTimeRef.current.getTime();
      const remaining = Math.max(0, autoRefreshInterval - elapsed);
      setTimeUntilNextRefresh(remaining);

      if (remaining === 0) {
        lastRefreshTimeRef.current = new Date();
        setTimeUntilNextRefresh(autoRefreshInterval);
      }
    };

    countdownTimerRef.current = setInterval(updateCountdown, 1000);
    updateCountdown();
  }, [autoRefreshEnabled, autoRefreshInterval]);

  const forceRefresh = useCallback(async () => {
    try {
      await fetchOrganizations({ force: true });
    } catch {
      // swallow; timestamp update still triggers UI refresh points/timers
    }
    const now = new Date();
    setLastUpdated(now);
    lastRefreshTimeRef.current = now;
    startCountdownTimer();
  }, [fetchOrganizations, startCountdownTimer]);

  useEffect(() => {
    if (!autoRefreshEnabled) {
      if (autoRefreshTimerRef.current) {
        clearInterval(autoRefreshTimerRef.current);
        autoRefreshTimerRef.current = null;
      }
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
      setTimeUntilNextRefresh(0);
      return;
    }

    startCountdownTimer();

    autoRefreshTimerRef.current = setInterval(() => {
      if (document.visibilityState === 'visible') {
        void (async () => {
          try {
            await fetchOrganizations({ force: true });
          } finally {
            const now = new Date();
            setLastUpdated(now);
            lastRefreshTimeRef.current = now;
          }
        })();
      }
    }, autoRefreshInterval);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && autoRefreshEnabled) {
        startCountdownTimer();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (autoRefreshTimerRef.current) {
        clearInterval(autoRefreshTimerRef.current);
      }
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [autoRefreshEnabled, autoRefreshInterval, fetchOrganizations, startCountdownTimer]);

  return (
    <AppContext.Provider
      value={{
        houses: state.houses,
        activeHouses,
        transactions: state.transactions,
        config: state.config,
        getHouse,
        updateHouse,
        setActiveHouseCount,
        addPoints,
        getTransactions,
        getMascotUserId,
        lastUpdated,
        autoRefreshEnabled,
        autoRefreshInterval,
        timeUntilNextRefresh,
        setAutoRefreshEnabled,
        setAutoRefreshInterval,
        forceRefresh,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}
